import React, { useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import './PolicyMISData.css';

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(v) {
  if (!v) return '-';
  try {
    return new Date(v).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return v;
  }
}

function fmtDateTime(v) {
  if (!v) return '-';
  try {
    return new Date(v).toLocaleString('th-TH', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  } catch {
    return v;
  }
}

const POLICY_TYPE_LABELS = {
  MOTOR_INSURANCE: 'ประกันภาคสมัครใจ',
  COMPULSORY_MOTOR_INSURANCE: 'พรบ. / ประกันภาคบังคับ',
  MOTOR_ADD_ON: 'Motor Add-On',
};

export default function PolicyMISData() {
  const [propId, setPropId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const handleSearch = async () => {
    const trimmed = propId.trim();
    if (!trimmed) {
      setError('กรุณาระบุ Prop ID');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/apfs/submissions/${encodeURIComponent(trimmed)}/mis-preview`);
      if (response.data.success) {
        setResult(response.data);
      } else {
        setError(response.data.error || 'ไม่พบข้อมูล');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const misData = result?.misData;
  const policy = misData?.data?.[0];
  const logs = result?.callbackLogs || [];

  return (
    <div className="pmis-wrap">
      <div className="pmis-header">
        <h2>L.13 Get Policy MIS Data API</h2>
        <p className="pmis-subtitle">
          ดูข้อมูล MIS ที่เราจะส่งกลับให้ APFS เมื่อ APFS เรียก callback มาหาเรา
          <span className="pmis-note">** API URL มาจากฝั่ง Partner (Click)</span>
        </p>
      </div>

      <div className="pmis-form">
        <label htmlFor="pmis-propId">Prop ID</label>
        <div className="pmis-input-row">
          <input
            id="pmis-propId"
            type="text"
            value={propId}
            onChange={(e) => { setPropId(e.target.value); if (error) setError(''); }}
            onKeyDown={handleKeyDown}
            placeholder="เลขกรมธรรม์ชั่วคราว หรือ เลข refId"
            autoComplete="off"
          />
          <button className="pmis-btn-search" onClick={handleSearch} disabled={loading}>
            {loading ? 'กำลังค้นหา...' : 'ค้นหา'}
          </button>
        </div>
        <p className="pmis-hint">เลขกรมธรรม์ชั่วคราว หรือ เลข refId ที่ Click ส่งมาตอนสร้างสัญญากับ APFS ในครั้งแรก</p>
      </div>

      {error && (
        <div className="pmis-error">
          <span>⚠️</span> {error}
        </div>
      )}

      {misData && (
        <div className="pmis-result">

          {/* Response preview section */}
          <div className="pmis-section">
            <div className="pmis-section-title">
              <span className="pmis-badge-inbound">INBOUND CALLBACK</span>
              ข้อมูลที่เราจะส่งกลับให้ APFS (L.13 Response)
            </div>

            <div className="pmis-response-meta">
              <span className="pmis-meta-item"><b>Prop ID:</b> {misData.propId}</span>
              <span className="pmis-meta-item"><b>Endpoint:</b> POST /api/apfs/callback/mis-data</span>
              <span className="pmis-meta-item"><b>พบข้อมูล:</b> {misData.data.length} record</span>
            </div>

            {policy ? (
              <div className="pmis-policy-card">
                {/* Policy identity */}
                <div className="pmis-card-section">
                  <div className="pmis-card-section-title">ข้อมูลกรมธรรม์</div>
                  <div className="pmis-grid">
                    <div className="pmis-field">
                      <span className="pmis-field-label">Insurer Code</span>
                      <span className="pmis-field-val bold">{policy.insurerCode || '-'}</span>
                    </div>
                    <div className="pmis-field">
                      <span className="pmis-field-label">Policy No</span>
                      <span className="pmis-field-val bold">{policy.policyNo || '-'}</span>
                    </div>
                    <div className="pmis-field">
                      <span className="pmis-field-label">Policy Type</span>
                      <span className="pmis-field-val">
                        {policy.policyType || '-'}
                        {POLICY_TYPE_LABELS[policy.policyType] && (
                          <span className="pmis-sub"> ({POLICY_TYPE_LABELS[policy.policyType]})</span>
                        )}
                      </span>
                    </div>
                    <div className="pmis-field">
                      <span className="pmis-field-label">Policy URL</span>
                      <span className="pmis-field-val">
                        {policy.policyUrl
                          ? <a href={policy.policyUrl} target="_blank" rel="noopener noreferrer" className="pmis-link">{policy.policyUrl}</a>
                          : '-'}
                      </span>
                    </div>
                    <div className="pmis-field">
                      <span className="pmis-field-label">วันเริ่มต้นกรมธรรม์</span>
                      <span className="pmis-field-val">{fmtDate(policy.policyStartDate)}</span>
                    </div>
                    <div className="pmis-field">
                      <span className="pmis-field-label">วันสิ้นสุดกรมธรรม์</span>
                      <span className="pmis-field-val">{fmtDate(policy.policyEndDate)}</span>
                    </div>
                  </div>
                </div>

                {/* Vehicle info */}
                <div className="pmis-card-section">
                  <div className="pmis-card-section-title">ข้อมูลรถยนต์</div>
                  <div className="pmis-grid">
                    <div className="pmis-field">
                      <span className="pmis-field-label">ยี่ห้อ (makeC)</span>
                      <span className="pmis-field-val">{policy.makeC || '-'}</span>
                    </div>
                    <div className="pmis-field">
                      <span className="pmis-field-label">รุ่น (model)</span>
                      <span className="pmis-field-val">{policy.model || '-'}</span>
                    </div>
                    <div className="pmis-field">
                      <span className="pmis-field-label">ปีผลิต</span>
                      <span className="pmis-field-val">{policy.yearOfManufacture || '-'}</span>
                    </div>
                    <div className="pmis-field">
                      <span className="pmis-field-label">ทะเบียนรถ</span>
                      <span className="pmis-field-val bold">{policy.licensePlateNo || '-'}</span>
                    </div>
                    <div className="pmis-field pmis-field-full">
                      <span className="pmis-field-label">เลขตัวถัง (VIN)</span>
                      <span className="pmis-field-val mono">{policy.vinNumber || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* Premium info */}
                <div className="pmis-card-section">
                  <div className="pmis-card-section-title">ข้อมูลเบี้ยประกัน</div>
                  <div className="pmis-grid pmis-grid-4">
                    <div className="pmis-field pmis-field-premium">
                      <span className="pmis-field-label">Gross Premium</span>
                      <span className="pmis-field-val bold">฿{fmtMoney(policy.transGrossPremium)}</span>
                    </div>
                    <div className="pmis-field pmis-field-premium">
                      <span className="pmis-field-label">Net Premium</span>
                      <span className="pmis-field-val bold">฿{fmtMoney(policy.transNetPremuim)}</span>
                    </div>
                    <div className="pmis-field pmis-field-premium">
                      <span className="pmis-field-label">Stamp Duty</span>
                      <span className="pmis-field-val">฿{fmtMoney(policy.transStampDuty)}</span>
                    </div>
                    <div className="pmis-field pmis-field-premium">
                      <span className="pmis-field-label">VAT</span>
                      <span className="pmis-field-val">฿{fmtMoney(policy.vat)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="pmis-empty">ไม่พบข้อมูล policy สำหรับ Prop ID นี้</div>
            )}
          </div>

          {/* L.13 Callback history */}
          <div className="pmis-section">
            <div className="pmis-section-title">
              ประวัติที่ APFS เรียก L.13 มาหาเรา
              <span className="pmis-count">{logs.length} ครั้ง</span>
            </div>

            {logs.length === 0 ? (
              <div className="pmis-empty">ยังไม่มี L.13 callback สำหรับ Prop ID นี้</div>
            ) : (
              <div className="pmis-table-wrap">
                <table className="pmis-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>เวลาที่รับ</th>
                      <th>Prop ID</th>
                      <th>Processed</th>
                      <th>Payload</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, i) => (
                      <tr key={log.id || i}>
                        <td className="text-center text-muted">{i + 1}</td>
                        <td>{fmtDateTime(log.received_at)}</td>
                        <td className="mono">{log.prop_id}</td>
                        <td className="text-center">
                          <span className={`pmis-pill ${log.processed ? 'pill-yes' : 'pill-no'}`}>
                            {log.processed ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="mono text-sm">{JSON.stringify(log.payload)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {result && (
        <div className="pmis-raw-section">
          <button className="pmis-btn-toggle" onClick={() => setShowRaw(!showRaw)}>
            {showRaw ? 'ซ่อน' : 'แสดง'} Raw Response
          </button>
          {showRaw && (
            <pre className="pmis-raw">{JSON.stringify(result, null, 2)}</pre>
          )}
        </div>
      )}
    </div>
  );
}
